import {
    PARKING_BRANCH_CONFIG,
    resolveParkingBranch,
    type ParkingBranchItem,
    type ParkingBranchSection,
} from '@/constants/parking/branch-parking-info';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import ForeignParkingRow from './ForeignParkingRow';
import ForeignParkingSection from './ForeignParkingSection';

interface ForeignParkingPanelProps {
    storeCode?: string;
}

function getItemKey(item: ParkingBranchItem): string {
    if (item.type === 'text') {
        return item.textKey;
    }

    return `${item.labelKey}:${item.valueKey}`;
}

function buildSectionAriaLabel(
    title: string,
    items: ParkingBranchItem[],
    t: TFunction
): string {
    const itemLabels = items.map((item) => {
        if (item.type === 'text') {
            return t(item.textKey);
        }
        return `${t(item.labelKey)} ${t(item.valueKey)}`;
    });

    return [title, ...itemLabels].join(', ');
}

function renderSectionItems(items: ParkingBranchItem[], t: TFunction) {
    return items.map((item) => {
        const itemKey = getItemKey(item);

        if (item.type === 'text') {
            return (
                <p
                    key={itemKey}
                    className="text-[calc(24*var(--vh-unit))] leading-[calc(26*var(--vh-unit))] font-medium tracking-[calc(-0.48*var(--vh-unit))] text-[color:var(--color-text-main01)]"
                >
                    {t(item.textKey)}
                </p>
            );
        }

        return (
            <ForeignParkingRow
                key={itemKey}
                label={t(item.labelKey)}
                value={t(item.valueKey)}
            />
        );
    });
}

function renderSection(section: ParkingBranchSection, t: TFunction) {
    const title = t(section.titleKey);

    return (
        <ForeignParkingSection
            key={section.titleKey}
            title={title}
            ariaLabel={buildSectionAriaLabel(title, section.items, t)}
        >
            {renderSectionItems(section.items, t)}
        </ForeignParkingSection>
    );
}

export default function ForeignParkingPanel({ storeCode }: ForeignParkingPanelProps) {
    const { t } = useTranslation();
    const branch = resolveParkingBranch(storeCode);
    const sections = PARKING_BRANCH_CONFIG[branch];

    return (
        <div className="w-[calc(872*var(--vw-unit))] shrink-0 rounded-[calc(20*var(--vh-unit))] bg-[var(--color-bg-parking-foreign-panel)] px-[calc(60*var(--vw-unit))] py-[calc(72*var(--vh-unit))]">
            <div
                className="flex w-full flex-col gap-[calc(80*var(--vh-unit))]"
                data-region-nav
                data-region-order={2}
            >
                {sections.map((section) => renderSection(section, t))}
            </div>
        </div>
    );
}
